
import React, { useState } from 'react';
import RoleSelection from '../components/RoleSelection';
import { useGame } from '../contexts/GameContext';
import CareerDashboardTabs from '../components/career/CareerDashboardTabs';
import OnboardingTutorial from '../components/onboarding/OnboardingTutorial';
import { Button } from '@/components/ui/button';
import { HelpCircle, RefreshCcw, PlayCircle } from 'lucide-react';
import CompletionScreen from '../components/career/CompletionScreen';
import CareerHeader from '../components/career/CareerHeader';
import YearProgress from '../components/career/YearProgress';
import YearCountdown from '../components/career/YearCountdown';
import CareerModals from '../components/career/CareerModals';
import ChallengeEvent from '../components/ChallengeEvent';
import MilestoneNotification from '../components/career/MilestoneNotification';
import { useCareerSimulation } from '../hooks/useCareerSimulation'; // This import stays the same
import { useMilestoneTracking } from '../hooks/useMilestoneTracking';
import { useCareerPathSelection } from '../hooks/useCareerPathSelection';

const CareerPath = () => {
  const { 
    gameStarted, 
    hasTutorialCompleted, 
    setHasTutorialCompleted,
    isSimulationComplete,
    startGame,
    yearsPassed,
    currentChallenge,
    currentSpecialEvent,
    completeSpecialEvent,
    careerTitle
  } = useGame();
  
  const [showTutorial, setShowTutorial] = useState(!hasTutorialCompleted);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showSpecialEventModal, setShowSpecialEventModal] = useState(false);

  const { yearProgress, isSimulating, simulateTimePassage, forceResetSimulation, timeLeft, yearDuration } = useCareerSimulation();
  const { currentMilestone } = useMilestoneTracking();
  const { showPathSelection, setShowPathSelection, canSelectPath } = useCareerPathSelection();

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    setHasTutorialCompleted(true);
  };
  
  const handleRestart = () => {
    setShowCompletion(false);
    // Navigate back to role selection
  };

  // Handle special events
  React.useEffect(() => {
    if (currentSpecialEvent && !showSpecialEventModal) {
      setShowSpecialEventModal(true);
    }
  }, [currentSpecialEvent, showSpecialEventModal]);
  
  // Show the decision modal when there's a new challenge
  React.useEffect(() => {
    if (currentChallenge && !showDecisionModal) {
      setShowDecisionModal(true);
    }
  }, [currentChallenge, showDecisionModal]);

  // Effect to show completion screen when simulation completes
  React.useEffect(() => {
    if (isSimulationComplete) {
      setShowCompletion(true);
    }
  }, [isSimulationComplete]);

  const handleCloseDecisionModal = () => {
    setShowDecisionModal(false);
  };
  
  const handleCloseSpecialEventModal = () => {
    setShowSpecialEventModal(false);
    completeSpecialEvent();
  };

  return (
    <div className="min-h-screen bg-background">
      {!gameStarted ? (
        showTutorial ? (
          <div className="container mx-auto py-8 px-4">
            <OnboardingTutorial onComplete={handleTutorialComplete} />
          </div>
        ) : (
          <div className="container mx-auto py-8 px-4">
            <div className="flex justify-end mb-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowTutorial(true)}
                className="mr-2"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                Show Tutorial
              </Button>
            </div>
            <RoleSelection />
          </div>
        )
      ) : showCompletion ? (
        <div className="container mx-auto py-8 px-4">
          <CompletionScreen onRestart={handleRestart} />
        </div>
      ) : (
        <div className="container mx-auto py-6 px-4">
          <div className="mb-4">
            <CareerHeader 
              currentTitle={careerTitle}
              yearsPassed={yearsPassed}
              isSimulating={isSimulating}
              showDecisionModal={showDecisionModal}
              currentChallenge={currentChallenge}
              currentSpecialEvent={currentSpecialEvent}
              onSimulate={simulateTimePassage}
              onShowChallenge={() => setShowDecisionModal(true)}
              onShowSpecialEvent={() => setShowSpecialEventModal(true)}
            />
          </div>
          
          <div className="flex justify-end mb-4 space-x-3">
            <Button
              variant="default"
              size="sm"
              onClick={simulateTimePassage}
              disabled={isSimulating || Boolean(currentChallenge) || Boolean(currentSpecialEvent)}
              className={isSimulating ? "animate-pulse" : ""}
            >
              <PlayCircle className="mr-2 h-4 w-4" />
              {isSimulating ? "Simulating..." : "Start Simulation"}
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={forceResetSimulation}
              title="Use this if the simulation gets stuck"
            >
              <RefreshCcw className="mr-2 h-4 w-4" />
              Reset Simulation
            </Button>
          </div>
          
          {/* Show either the year countdown timer or progress bar based on simulation state */}
          {isSimulating ? (
            <YearCountdown 
              timeLeft={timeLeft} 
              totalTime={yearDuration}
              isSimulating={isSimulating}
            />
          ) : (
            <YearProgress 
              yearProgress={yearProgress} 
              yearsPassed={yearsPassed} 
            />
          )}
          
          <div className="mt-6">
            <CareerDashboardTabs />
          </div>
          
          <CareerModals
            showDecisionModal={showDecisionModal}
            showSpecialEventModal={showSpecialEventModal}
            showPathSelection={showPathSelection}
            canSelectPath={canSelectPath}
            onCloseDecision={handleCloseDecisionModal}
            onCloseSpecialEvent={handleCloseSpecialEventModal}
            onClosePathSelection={() => setShowPathSelection(false)}
          />
          
          {currentChallenge && <ChallengeEvent challenge={currentChallenge} />}
          
          <MilestoneNotification milestone={currentMilestone} />
        </div>
      )}
    </div>
  );
};

export default CareerPath;
